import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { _T } from "../../base-react/src/Localization";
import { Layout, Menu, Select, Space, Tooltip, Typography } from 'antd';
import { helpers, hooks, models } from '@vote4dance/judging-sdk';
var Stream = hooks.streams.Stream;
var ScheduleRoundSource = models.rounds.ScheduleRoundSource;
var ScheduleSource = models.schedules.ScheduleSource;
import { Link, useLocation } from 'react-router-dom';
import { getFormattedTime, useSchedulePages } from '../../base-react/hooks/Schedule';
import { RoundStatus } from './RoundStatus';
import { RoundPathParent } from './RoundPathParent';
import { RoundItem } from '../../base-react/models/rounds';
import { TeamOutlined } from '@ant-design/icons';
var groupRoundListIntoChildren = helpers.groupRoundListIntoChildren;
import { useUserSettings } from '../../base-react/hooks/Storage';
import { RoundType } from '@vote4dance/judging-schema';
const { Text } = Typography;
export const Upcoming = ({ event_id, makeRoundPath }) => {
    const [schedule, loading] = Stream(ScheduleSource(event_id));
    const { pages, pageData, defaultPageId } = useSchedulePages(event_id, schedule);
    const { settings, updateSettings } = useUserSettings();
    const [currentPage, setCurrentPage] = useState();
    const lastAppliedShowPage = useRef(undefined);
    useEffect(() => {
        let showPageId = defaultPageId;
        const userPageInfo = settings.managerRightSiderPageInfo;
        if (userPageInfo) {
            const userPageIndexParts = userPageInfo.split('/');
            if (userPageIndexParts.length === 2) {
                const storedEventId = userPageIndexParts[0];
                const storedPageId = userPageIndexParts[1];
                if (storedEventId === event_id && storedPageId) {
                    showPageId = storedPageId;
                }
            }
        }
        if (!showPageId) {
            lastAppliedShowPage.current = undefined;
            return;
        }
        if (showPageId === lastAppliedShowPage.current) {
            return;
        }
        lastAppliedShowPage.current = showPageId;
        setCurrentPage(showPageId);
    }, [defaultPageId, settings.managerRightSiderPageInfo, event_id]);
    useEffect(() => {
        // Save the current page info in user settings whenever the current page changes
        if (currentPage) {
            const newValue = `${event_id}/${currentPage}`;
            // Avoid unnecessary updates
            if (settings.managerRightSiderPageInfo !== newValue) {
                updateSettings({ managerRightSiderPageInfo: newValue });
            }
        }
    }, [currentPage, event_id, settings.managerRightSiderPageInfo, updateSettings]);
    const pageIndex = pages.findIndex(page => page.schedule_id == currentPage);
    const firstIndexWithRounds = pageData[pageIndex] ? pageData[pageIndex].findIndex(item => item.schedule_rounds_total > 0) : 0;
    const filteredPageData = pageData[pageIndex]
        ? pageData[pageIndex].filter((item, index) => item.schedule_rounds_total > 0 ? item.schedule_rounds_confirmed != item.schedule_rounds_total : index >= firstIndexWithRounds)
        : [];
    const [pageMenuItems, setPageMenuItems] = useState({});
    const handlePageMenuItemsChange = useCallback((scheduleId, items) => {
        setPageMenuItems((prev) => {
            if (prev[scheduleId] === items) {
                return prev;
            }
            return { ...prev, [scheduleId]: items };
        });
    }, []);
    const menuItems = useMemo(() => {
        if (!(filteredPageData === null || filteredPageData === void 0 ? void 0 : filteredPageData.length)) {
            return [];
        }
        const entries = [];
        filteredPageData.forEach((page, index) => {
            var _a;
            const scheduleId = page.schedule_id;
            if (!scheduleId) {
                return;
            }
            entries.push({
                key: `group-${scheduleId}`,
                type: 'group',
                label: (React.createElement(Tooltip, { placement: 'left', title: settings.managerRightSiderCollapsed &&
                        page.schedule_value + ' ' + getFormattedTime(page.schedule_start_time) },
                    page.schedule_value,
                    " ",
                    getFormattedTime(page.schedule_start_time))),
                children: (_a = pageMenuItems[scheduleId]) !== null && _a !== void 0 ? _a : [],
            });
            if (index < filteredPageData.length - 1) {
                entries.push({ type: 'divider', key: `divider-${scheduleId}` });
            }
        });
        return entries;
    }, [filteredPageData, pageMenuItems, settings.managerRightSiderCollapsed]);
    if (!currentPage) {
        return null;
    }
    return (React.createElement("div", { className: "manager-right-upcoming", style: {
            overflow: 'auto',
            height: '100vh',
            position: 'sticky',
            right: 0,
            top: 0,
            bottom: 0,
        } },
        React.createElement(Layout.Sider, { width: 200, collapsedWidth: 60, collapsible: true, collapsed: settings.managerRightSiderCollapsed, onCollapse: (collapsed, type) => type === 'clickTrigger' && updateSettings({ managerRightSiderCollapsed: collapsed }), reverseArrow: true },
            React.createElement("div", { style: { display: 'flex', flexDirection: 'column', height: '100%' } },
                React.createElement(Select, { loading: loading, style: { width: '100%' }, value: currentPage, onChange: setCurrentPage, options: pages && pages.map((page) => ({
                        value: page.schedule_id,
                        label: (React.createElement(Tooltip, { placement: 'left', title: settings.managerRightSiderCollapsed && page.schedule_value }, page.schedule_value))
                    })) }),
                React.createElement(Menu, { selectable: false, items: menuItems, style: { flex: 1, overflowY: 'auto' } }),
                filteredPageData.map((page) => (React.createElement(SchedulePageItems, { key: page.schedule_id, page: page, collapsed: settings.managerRightSiderCollapsed, makeRoundPath: makeRoundPath, onItemsChange: handlePageMenuItemsChange })))))));
};
const SchedulePageItems = ({ page, collapsed, makeRoundPath, onItemsChange }) => {
    const [items] = Stream(ScheduleRoundSource(page.competition_id, page.schedule_id));
    const filteredItems = useMemo(() => {
        return items ? items.filter(a => a.round_status != 'confirmed') : [];
    }, [items]);
    const hasBeats = useMemo(() => items === null || items === void 0 ? void 0 : items.some(item => item.round_beat !== null), [items]);
    const hasFloors = useMemo(() => items === null || items === void 0 ? void 0 : items.some(item => item.floor_id !== null), [items]);
    const groupedItems = useMemo(() => groupRoundListIntoChildren(filteredItems), [filteredItems]);
    const location = useLocation();
    const parts = location.pathname.split('/');
    const selectedRoute = parts[parts.length - 1];
    const defaultMakeRoundPath = useCallback((item) => {
        const idToUse = (parseInt(item.round_parent_id, 10) > 0 && item.round_type === RoundType.Normal)
            ? item.round_parent_id
            : item.round_id;
        return `comp/${item.competition_id}/classes/${item.class_id}/${idToUse}`;
    }, []);
    const buildPath = useCallback((item) => {
        const raw = (makeRoundPath !== null && makeRoundPath !== void 0 ? makeRoundPath : defaultMakeRoundPath)(item);
        return raw.replace(/([^:]\/)\/+/g, '$1');
    }, [makeRoundPath, defaultMakeRoundPath]);
    const menuEntries = useMemo(() => {
        if (!groupedItems || groupedItems.length === 0) {
            return [];
        }
        return groupedItems.map((item, index) => {
            var _a, _b;
            return ({
                key: `${(_a = page.schedule_id) !== null && _a !== void 0 ? _a : 'unknown'}-${(_b = item.round_id) !== null && _b !== void 0 ? _b : 'item'}-${index}`,
                label: (React.createElement(Link, { to: buildPath(item) },
                    React.createElement(Space, { style: { width: '100%' } },
                        item.round_new_teams > 0 ? (React.createElement(Tooltip, { title: _T('round.status.published.title') },
                            React.createElement(TeamOutlined, null))) : (React.createElement(RoundStatus, { round: item })),
                        (hasBeats || hasFloors) && React.createElement("strong", null,
                            item.round_beat,
                            item.floor_code),
                        React.createElement(RoundPathParent, { record: item }, (round, parents) => (React.createElement(Space, null,
                            parents
                                ? [...parents].reverse().map((parent) => (React.createElement("div", { key: parent.round_id }, RoundItem.getRoundLabel(parent))))
                                : RoundItem.getRoundLabel(round),
                            React.createElement(Text, { type: "secondary", style: collapsed ? undefined : {
                                    position: 'absolute',
                                    left: 20,
                                    top: 25,
                                    fontSize: 'x-small'
                                } }, RoundItem.getClassLabel(round)))))))),
                danger: String(item.round_id) === selectedRoute,
                style: { paddingInline: 0 },
            });
        });
    }, [buildPath, collapsed, groupedItems, hasBeats, hasFloors, page.schedule_id, selectedRoute]);
    const scheduleId = page.schedule_id;
    useEffect(() => {
        if (!scheduleId) {
            return;
        }
        onItemsChange(scheduleId, menuEntries);
    }, [menuEntries, onItemsChange, scheduleId]);
    return null;
};
